import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bot } from 'lucide-react'
import { assistentBereichFuerPfad } from '@symphenia/shared-types'
import { assistentApi } from '@/lib/assistent/assistent.api'
import { useAssistent, useDarfAssistent } from '@/lib/assistent/assistent.store'

/**
 * AP-18 5.3 — das Eingabefeld **„Frag den Assistenten …"** auf den Dashboards, darunter bis zu drei
 * Beispielfragen des Bereichs. Absenden öffnet die Seitenleiste mit der Frage; den Bereich nimmt die
 * Leiste aus der Adresse der Seite, wie bei jeder Frage.
 *
 * Das Feld steht nur da, wenn der Benutzer den Assistenten hat **und** der gerade Fragen annimmt
 * (eingeschaltet, Schlüssel oder Kontingent da) — ein Feld, dessen Antwort „frag deinen Administrator"
 * wäre, stünde sonst auf jedem Dashboard. Warum er nichts annimmt, sagt die Leiste hinter dem Symbol
 * im Menükopf.
 *
 * Die Beispielfragen stellt der Server zusammen (`GET /ai/assistant/examples`): aus den Werkzeugen,
 * die der Benutzer hat, und den freigegebenen Themen des Handbuchs. Ohne Beispiele bleibt das Feld.
 */
export function AssistentFrage({ className = '' }: { className?: string }) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const darf = useDarfAssistent()
  const stand = useAssistent((s) => s.stand)
  const ladeStand = useAssistent((s) => s.ladeStand)
  const oeffnen = useAssistent((s) => s.oeffnen)
  const [text, setText] = useState('')
  const [beispiele, setBeispiele] = useState<string[]>([])
  const bereich = assistentBereichFuerPfad(pathname)

  // Der Stand kommt sonst erst mit der geöffneten Leiste — einmal je Sitzung reicht, sie hält ihn danach frisch.
  const gefragt = useRef(false)
  useEffect(() => {
    if (!darf || stand || gefragt.current) return
    gefragt.current = true
    void ladeStand()
  }, [darf, stand, ladeStand])

  const bereit = darf && stand?.ready === true
  useEffect(() => {
    if (!bereit) return
    let weg = false
    void assistentApi
      .beispiele(bereich)
      .then((b) => {
        if (!weg) setBeispiele(b)
      })
      // Ohne Beispiele bleibt das Feld — es ist nicht der Ort für eine Fehlermeldung.
      .catch(() => undefined)
    return () => {
      weg = true
    }
  }, [bereit, bereich])

  if (!bereit) return null

  const fragen = (frage: string) => {
    const f = frage.trim()
    if (!f) return
    oeffnen({ frage: f })
    setText('')
  }

  return (
    <form
      className={className}
      role="search"
      aria-label={t('assistant.title')}
      onSubmit={(e) => {
        e.preventDefault()
        fragen(text)
      }}
    >
      <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 focus-within:border-primary">
        <Bot className="h-4 w-4 shrink-0 text-primary" aria-hidden />
        <input
          className="min-w-0 flex-1 bg-transparent py-1 text-sm outline-none placeholder:text-muted-foreground"
          placeholder={t('assistant.dashboard.placeholder')}
          aria-label={t('assistant.dashboard.placeholder')}
          value={text}
          maxLength={4000}
          onChange={(e) => setText(e.target.value)}
        />
        <button type="submit" className="shrink-0 rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50" disabled={!text.trim()}>
          {t('assistant.ask')}
        </button>
      </div>
      {beispiele.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={t('assistant.dashboard.examples')}>
          {beispiele.map((b) => (
            <button key={b} type="button" className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover:border-primary hover:text-foreground" onClick={() => fragen(b)}>
              {b}
            </button>
          ))}
        </div>
      )}
    </form>
  )
}
